import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { GUID, Tenant } from '@ndvr/customer-api';
import { Account, AccountInheritedRmdUpdateRequest } from '@ndvr/portfolio-api';

import { FilterType, Transaction } from '@/cip/_openapi/portfolio-optimizer';
import { getTransactions } from '@/cip/api/transaction';
import { PortfolioApi } from 'API';
import { useAuthContext } from 'views/auth/Auth';
import { Toast } from 'widgets';

const RmdQueryKeys = {
    rmdByTenants: (tenantGUIDs: GUID<Tenant>[]) => ['rmd', 'tenant', [...tenantGUIDs].sort()],
} as const;

export const useRmdByTenants = () => {
    const { selectedTenants } = useAuthContext();
    const tenantGUIDs = useMemo(() => selectedTenants.map((tenant) => tenant.tenantGUID), [selectedTenants]);
    return useQuery({
        queryKey: RmdQueryKeys.rmdByTenants(tenantGUIDs),
        enabled: tenantGUIDs.length > 0,
        queryFn: async () => {
            const [response] = await PortfolioApi().getRmdInfoForAccountOwners(tenantGUIDs);
            return response;
        },
    });
};

/**
 * Fetch cash transactions for all accounts belonging to an RMD owner group,
 * filtered to the current tax year. Enabled only when the row is expanded.
 */
export const useRmdTransactions = (accountGUIDs: GUID<Account>[], taxYear: number, enabled: boolean) => {
    return useQuery({
        queryKey: ['rmd', 'transactions', accountGUIDs, taxYear],
        enabled: enabled && accountGUIDs.length > 0,
        placeholderData: keepPreviousData,
        queryFn: async (): Promise<{ transactions: Transaction[]; totalNr: number }> => {
            const page = await getTransactions({
                filter: [
                    // Filter to the specific accounts
                    {
                        type: FilterType.LIST,
                        field: 'portfolio_guid',
                        value: accountGUIDs.map((guid) => ({
                            type: FilterType.STRING,
                            field: 'portfolio_guid',
                            value: guid,
                        })),
                    },
                    // RMD-satisfying transaction types (mirrors Python service get_yearly_distributions)
                    {
                        type: FilterType.LIST,
                        field: 'type',
                        value: [
                            { type: FilterType.STRING, field: 'type', value: 'Withdrawal' },
                            { type: FilterType.STRING, field: 'type', value: 'Transfer Out' },
                        ],
                    },
                    // Current tax year date range
                    {
                        type: FilterType.DATE,
                        field: 'trade_date',
                        value: { from: `${taxYear}-01-01`, to: `${taxYear}-12-31` },
                    },
                ],
                pageSize: 200,
                pageIndex: 0,
                sortLabel: 'trade_date',
                sortDesc: true,
            });
            return { transactions: page.transactions ?? [], totalNr: page.totalNr };
        },
    });
};

export const useUpdateInheritedRmdProperties = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            accountGUID,
            request,
        }: {
            accountGUID: GUID<Account>;
            request: AccountInheritedRmdUpdateRequest;
        }) => {
            await PortfolioApi().updateInheritedRmdProperties(accountGUID, request);
        },
        onSuccess: (_data, { accountGUID }) => {
            toast.success('Beneficiary & decedent info saved');
            queryClient.invalidateQueries({ queryKey: ['account', accountGUID] });
            queryClient.invalidateQueries({ queryKey: ['rmd'] });
        },
        onError: (error) => Toast.forError(error, 'Failed to save beneficiary info'),
    });
};
